import { useAuth } from '@clerk/clerk-react'
import { useState } from 'react'

import { DigestFields } from '@/components/digest/DigestFields'
import { Button } from '@/components/ui/Button'
import { GlassCard } from '@/components/ui/GlassCard'
import { Pill } from '@/components/ui/Pill'
import { useMe } from '@/context/MeProvider'
import { ApiError } from '@/lib/api/client'
import { updateMyPreferences } from '@/lib/api/me'
import type { Me } from '@/lib/types/me'

export function DigestPreferences({ me }: { me: Me }) {
  const { getToken } = useAuth()
  const { refresh } = useMe()

  const neverAsked = me.digestOptIn === null || me.digestOptIn === undefined
  const [optIn, setOptIn] = useState(me.digestOptIn ?? false)
  const [categories, setCategories] = useState<string[]>(
    me.digestCategories ?? [],
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNotice, setSavedNotice] = useState(false)

  async function handleSave() {
    setSaving(true)
    setError(null)
    setSavedNotice(false)
    try {
      await updateMyPreferences(
        { digestOptIn: optIn, digestCategories: optIn ? categories : [] },
        getToken,
      )
      await refresh()
      setSavedNotice(true)
    } catch (err) {
      setError(
        err instanceof ApiError ? err.detail : "Couldn't save that just now.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <GlassCard
      as="section"
      aria-labelledby="digest-prefs-heading"
      className="flex flex-col gap-6 p-6 md:p-8"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2
          id="digest-prefs-heading"
          className="font-display text-headline-md text-fg"
        >
          Weekly digest
        </h2>
        {me.digestOptIn === true && <Pill tone="primary">Subscribed</Pill>}
      </div>

      {neverAsked && (
        <p className="font-body text-body-sm text-fg-muted">
          Want the newest links in your inbox each week?
        </p>
      )}

      <DigestFields
        optIn={optIn}
        categories={categories}
        onOptInChange={setOptIn}
        onCategoriesChange={setCategories}
        disabled={saving}
      />

      {error && (
        <p className="font-body text-body-sm text-error" role="alert">
          {error}
        </p>
      )}
      {savedNotice && (
        <p className="font-body text-body-sm text-fg-muted" aria-live="polite">
          Saved.
        </p>
      )}

      <Button
        type="button"
        variant="gradient"
        size="md"
        disabled={saving}
        className="self-start"
        onClick={() => void handleSave()}
      >
        {saving ? 'Saving…' : 'Save preferences'}
      </Button>
    </GlassCard>
  )
}
